import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { authApi, bettingApi, triageApi } from '../../api'
import { useEscapeKey } from '../../useEscapeKey'
import { ErrorState, errorMessage } from '../ErrorState'
import { TriageBadge } from '../TriageControls'
import { TRIAGE_STATUSES } from '../../types'
import type {
  BettingService,
  BettingTestFilters,
  BreachType,
  Carrier,
  ComplianceSeverity,
  PaginatedResponse,
  PaymentAggregator,
  PaymentTestDetail,
  PaymentTestRow,
  PaymentTestSource,
  TriageLogEntry,
  TriageStatus,
  TriageVocabulary, TestDirection} from '../../types'
import { periodRange, useCrumb, useShell } from '../../shell/ShellContext'
import { currentQuery, navigate, useRoute, withQuery } from '../../shell/router'
import { Icon } from '../../shell/icons'
import { Block, CollapsibleBlock, PageHeader, SidePanel, useRelativeDays } from '../../ui/ui'
import { enumLabel, translate, useI18n, useT } from '../../i18n'
import type { TFunction } from '../../i18n'
import {
  AggregatorStatusPill,
  BREACH_TYPES,
  breachLabel,
  depthLabel,
  EvidenceImage,
  FixtureBadge,
  FixtureToggle,
  RunStatusBadge,
  severityLabel,
  SEVERITY_ORDER,
  sourceLabel,
  SeverityBadge,
  countryLabel,
  formatDateTime,
  useBettingVocabulary,
} from './bettingShared'

/** Ordre de la dernière liste consultée, pour passer d'une fiche à la suivante. */
const LIST_ORDER_KEY = 'icc_betting_list_order'

function rememberOrder(ids: string[]) {
  try {
    sessionStorage.setItem(LIST_ORDER_KEY, JSON.stringify(ids))
  } catch {
    /* navigation précédent/suivant indisponible, rien de bloquant */
  }
}

function readOrder(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(LIST_ORDER_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

const FILTER_KEYS = ['serviceId', 'carrierId', 'aggregatorId', 'severity', 'breachType', 'triageStatus', 'source', 'open', 'search'] as const

// ── Liste ────────────────────────────────────────────────────────────────────

export function TestList() {
  useRoute() // relit les filtres de l'ancre à chaque changement
  const { t, fmt } = useI18n()
  const { country, period, showFixtures, setShowFixtures } = useShell()
  const vocab = useBettingVocabulary()
  const query = currentQuery()
  const filters: BettingTestFilters = {}
  for (const k of FILTER_KEYS) {
    const v = query.get(k)
    if (v) (filters as Record<string, string>)[k] = v
  }
  const page = Number(query.get('page')) || 1
  const range = periodRange(period)
  const request: BettingTestFilters = {
    ...filters,
    country: country || undefined,
    from: range.from,
    to: range.to,
    includeFixtures: showFixtures ? '1' : undefined,
    page,
    limit: 25,
  }
  const requestKey = JSON.stringify(request)

  const [data, setData] = useState<PaginatedResponse<PaymentTestRow> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState(filters.search ?? '')

  const setFilters = (patch: Partial<Record<(typeof FILTER_KEYS)[number] | 'page', string | undefined>>) => {
    const next: Record<string, string | undefined> = { ...(filters as Record<string, string>), page: undefined, ...patch }
    navigate(withQuery('betting/tests', next))
  }

  const load = useCallback(() => {
    setError(null)
    bettingApi
      .listTests(JSON.parse(requestKey) as BettingTestFilters)
      .then((res) => {
        setData(res)
        rememberOrder(res.data.map((t) => t.id))
      })
      .catch((err) => setError(errorMessage(err)))
  }, [requestKey])

  useEffect(load, [load])

  // Des parcours automatiques peuvent être en cours : on rafraîchit tant qu'il y en a.
  useEffect(() => {
    if (!data?.data.some((t) => t.runStatus === 'PENDING' || t.runStatus === 'RUNNING')) return
    const timer = setInterval(load, 6000)
    return () => clearInterval(timer)
  }, [data, load])

  const activeCount = FILTER_KEYS.filter((k) => filters[k as keyof BettingTestFilters]).length

  return (
    <>
      <PageHeader
        title={t('bet.testsTitle')}
        description={t('bet.testsDesc')}
        actions={
          <>
            <FixtureToggle checked={showFixtures} onChange={setShowFixtures} />
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              {t('bet.manualTest')}
            </button>
          </>
        }
      />

      <TestFilterBar filters={filters} onChange={setFilters} country={country} />

      <Block flush>
        <div className="bet-list-toolbar">
          <span className="block-title">
            {data ? t('bet.testCount', { n: data.total }) : t('bet.testsHeading')}
            {activeCount > 0 && <span className="block-sub"> · {t('bet.activeFilters', { n: activeCount })}</span>}
          </span>
          <form
            className="bet-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault()
              setFilters({ search: search.trim() || undefined })
            }}
          >
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('bet.testSearchPlaceholder')}
              aria-label={t('bet.testSearchAria')}
            />
            <button type="submit" className="btn btn-secondary">{t('common.search')}</button>
          </form>
        </div>

        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !data ? (
          <div className="stat-skeleton" style={{ minHeight: 220, margin: 16 }} />
        ) : data.data.length === 0 ? (
          <div className="table-empty">{t('bet.noTestMatch')}</div>
        ) : (
          <div className="table-scroll">
            <table className="bet-table bet-table-clickable">
              <thead>
                <tr>
                  <th scope="col">{t('bet.colSeverity')}</th>
                  <th scope="col">{t('bet.colService')}</th>
                  <th scope="col">{t('bet.colBreach')}</th>
                  <th scope="col">{t('bet.colAggregators')}</th>
                  <th scope="col">{t('bet.colRunBy')}</th>
                  <th scope="col">{t('common.date')}</th>
                  <th scope="col">{t('bet.colTriage')}</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    onClick={() => navigate(`betting/tests/${row.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`betting/tests/${row.id}`)
                    }}
                  >
                    <td><SeverityBadge severity={row.severity} /></td>
                    <td>
                      <div className="bet-service-cell">
                        <strong>{row.serviceName}<FixtureBadge when={row.isFixture} /></strong>
                        <span>
                          {t('bet.testMeta', { n: row.testNumber, country: countryLabel(vocab, row.country, t) })}
                          {row.carrierName ? ` · ${row.carrierName}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>{row.breachType ? breachLabel(t, row.breachType) : <span className="bet-muted">—</span>}</td>
                    <td>
                      {row.aggregators.length === 0 ? (
                        <span className="bet-muted">—</span>
                      ) : (
                        <div className="bet-agg-cell">
                          {row.aggregators.slice(0, 2).map((a) => (
                            <span key={a.name} className={`bet-agg-chip bet-agg-${(a.status ?? 'UNKNOWN').toLowerCase()}`}>{a.name}</span>
                          ))}
                          {row.aggregators.length > 2 && <span className="bet-muted">+{row.aggregators.length - 2}</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="bet-service-cell">
                        <span className="bet-cell-strong">{sourceLabel(t, row.source)}</span>
                        {row.runStatus !== 'COMPLETED' && <RunStatusBadge status={row.runStatus} />}
                      </div>
                    </td>
                    <td className="num">{fmt.dateTime(row.createdAt)}</td>
                    <td>
                      <div className="bet-service-cell">
                        <TriageBadge status={row.triageStatus} />
                        <span>{row.assignedTo ?? t('bet.notAssigned')}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setFilters({ page: String(page - 1) })}>← {t('common.previous')}</button>
            <span className="page-info">{t('common.page', { page: data.page, pages: data.totalPages })}</span>
            <button disabled={page >= data.totalPages} onClick={() => setFilters({ page: String(page + 1) })}>{t('common.next')} →</button>
          </div>
        )}
      </Block>

      {creating && (
        <ManualTestPanel
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            navigate(`betting/tests/${id}`)
          }}
        />
      )}
    </>
  )
}

function TestFilterBar({
  filters,
  onChange,
  country,
}: {
  filters: BettingTestFilters
  onChange: (patch: Record<string, string | undefined>) => void
  country: string
}) {
  const t = useT()
  const [services, setServices] = useState<BettingService[]>([])
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [aggregators, setAggregators] = useState<PaymentAggregator[]>([])

  useEffect(() => {
    bettingApi.listServices().then(setServices).catch(() => undefined)
    bettingApi.listCarriers().then(setCarriers).catch(() => undefined)
    bettingApi.listAggregators().then(setAggregators).catch(() => undefined)
  }, [])

  const active = FILTER_KEYS.some((k) => k !== 'search' && filters[k as keyof BettingTestFilters])
  const status = filters.open ? 'OPEN' : (filters.triageStatus ?? '')

  return (
    <div className="bet-filters" role="group" aria-label={t('bet.filtersAria')}>
      <FilterSelect label={t('bet.colSeverity')} value={filters.severity ?? ''} onChange={(v) => onChange({ severity: v })}>
        <option value="">{t('bet.filterAllF')}</option>
        {SEVERITY_ORDER.map((s) => <option key={s} value={s}>{severityLabel(t, s)}</option>)}
      </FilterSelect>
      <FilterSelect label={t('bet.colTriage')} value={status} onChange={(v) => onChange(v === 'OPEN' ? { open: '1', triageStatus: undefined } : { open: undefined, triageStatus: v })}>
        <option value="">{t('bet.filterAllM')}</option>
        <option value="OPEN">{t('bet.filterNotClosed')}</option>
        {TRIAGE_STATUSES.map((s) => <option key={s} value={s}>{enumLabel(t, 'triage', s, s)}</option>)}
      </FilterSelect>
      <FilterSelect label={t('bet.colBreach')} value={filters.breachType ?? ''} onChange={(v) => onChange({ breachType: v })}>
        <option value="">{t('bet.filterAllF')}</option>
        {BREACH_TYPES.map((b) => <option key={b} value={b}>{breachLabel(t, b)}</option>)}
      </FilterSelect>
      <FilterSelect label={t('bet.colService')} value={filters.serviceId ?? ''} onChange={(v) => onChange({ serviceId: v })}>
        <option value="">{t('bet.filterAllM')}</option>
        {services.filter((s) => !country || s.country === country).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </FilterSelect>
      <FilterSelect label={t('bet.filterCarrier')} value={filters.carrierId ?? ''} onChange={(v) => onChange({ carrierId: v })}>
        <option value="">{t('bet.filterAllM')}</option>
        {carriers.filter((c) => !country || c.country === country).map((c) => (
          <option key={c.id} value={c.id}>{country ? c.name : `${c.name} (${c.country})`}</option>
        ))}
      </FilterSelect>
      <FilterSelect label={t('bet.filterAggregator')} value={filters.aggregatorId ?? ''} onChange={(v) => onChange({ aggregatorId: v })}>
        <option value="">{t('bet.filterAllM')}</option>
        {aggregators.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </FilterSelect>
      <FilterSelect label={t('bet.colRunBy')} value={filters.source ?? ''} onChange={(v) => onChange({ source: v as PaymentTestSource })}>
        <option value="">{t('bet.filterAllM')}</option>
        <option value="AUTO">{t('bet.source.AUTO')}</option>
        <option value="MANUAL">{t('bet.source.MANUAL')}</option>
      </FilterSelect>
      {active && (
        <button
          type="button"
          className="btn btn-secondary bet-filters-clear"
          onClick={() => onChange(Object.fromEntries(FILTER_KEYS.filter((k) => k !== 'search').map((k) => [k, undefined])))}
        >
          {t('bet.clearFilters')}
        </button>
      )}
    </div>
  )
}

function FilterSelect({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string | undefined) => void; children: React.ReactNode }) {
  return (
    <label className="bet-filter">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value || undefined)}>
        {children}
      </select>
    </label>
  )
}

export function ManualTestPanel({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const t = useT()
  const vocab = useBettingVocabulary()
  const [services, setServices] = useState<BettingService[]>([])
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [serviceId, setServiceId] = useState('')
  const [carrierId, setCarrierId] = useState('')
  const [direction, setDirection] = useState<TestDirection>('DEPOSIT')
  // Valeurs stockées telles quelles côté serveur : ce sont des données, pas
  // des libellés, et les traduire changerait ce qui est enregistré.
  const [adChannel, setAdChannel] = useState('Web')
  const [connection, setConnection] = useState('Mobile data')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    bettingApi.listServices().then((s) => setServices(s.filter((x) => x.isActive))).catch((err) => setError(errorMessage(err)))
    bettingApi.listCarriers().then(setCarriers).catch(() => undefined)
  }, [])

  const service = services.find((s) => s.id === serviceId)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const test = await bettingApi.createManualTest({ serviceId, carrierId: carrierId || null, adChannel, connection, direction })
      onCreated(test.id)
    } catch (err) {
      setError(errorMessage(err))
      setSaving(false)
    }
  }

  return (
    <SidePanel
      title={t('bet.manualTitle')}
      subtitle={t('bet.manualSub')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={!serviceId || saving} onClick={submit}>
            {saving ? t('bet.creating') : t('bet.createTest')}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <label>
          {t('bet.direction')}
          <select value={direction} onChange={(e) => setDirection(e.target.value as TestDirection)}>
            <option value="DEPOSIT">{t('bet.directionDeposit')}</option>
            <option value="WITHDRAWAL">{t('bet.directionWithdrawal')}</option>
          </select>
        </label>
        {direction === 'WITHDRAWAL' && (
          <p className="bet-hint">
            {t('bet.withdrawalHintA')} <strong>{t('bet.withdrawalHintVerb')}</strong> {t('bet.withdrawalHintB')}
          </p>
        )}
        <label>
          {t('bet.serviceTested')}
          <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setCarrierId('') }}>
            <option value="">{t('bet.pickService')}</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name} — {countryLabel(vocab, s.country, t)}</option>
            ))}
          </select>
        </label>
        <label>
          {t('bet.carrier')}
          <select value={carrierId} onChange={(e) => setCarrierId(e.target.value)} disabled={!service}>
            <option value="">{t('bet.notSpecified')}</option>
            {carriers.filter((c) => c.country === service?.country).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('bet.channel')}
          <select value={adChannel} onChange={(e) => setAdChannel(e.target.value)}>
            <option value="Web">{t('bet.channelWeb')}</option>
            <option value="App">{t('bet.channelApp')}</option>
            <option value="SMS">{t('bet.channelSms')}</option>
            <option value="Social">{t('bet.channelSocial')}</option>
          </select>
        </label>
        <label>
          {t('bet.connection')}
          <select value={connection} onChange={(e) => setConnection(e.target.value)}>
            <option value="Mobile data">{t('bet.connectionMobile')}</option>
            <option value="Wifi">{t('bet.connectionWifi')}</option>
          </select>
        </label>
        {error && <ErrorState message={error} compact />}
      </div>
    </SidePanel>
  )
}

// ── Fiche d'un test ──────────────────────────────────────────────────────────

export function TestDetailPage({ testId }: { testId: string }) {
  const { t, fmt } = useI18n()
  const vocab = useBettingVocabulary()
  const [test, setTest] = useState<PaymentTestDetail | null>(null)
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [zoomIndex, setZoomIndex] = useState<number | null>(null)

  useCrumb(test ? t('bet.testNumber', { n: test.testNumber }) : null)

  const load = useCallback(() => {
    bettingApi.getTest(testId).then(setTest).catch((err) => setError(errorMessage(err)))
  }, [testId])

  useEffect(() => {
    setTest(null)
    setError(null)
    load()
  }, [load])

  useEffect(() => {
    if (test?.country) bettingApi.listCarriers(test.country).then(setCarriers).catch(() => undefined)
  }, [test?.country])

  // Parcours automatique en cours : la fiche se met à jour d'elle-même.
  useEffect(() => {
    if (test?.runStatus !== 'PENDING' && test?.runStatus !== 'RUNNING') return
    const timer = setInterval(load, 5000)
    return () => clearInterval(timer)
  }, [test?.runStatus, load])

  const save = async (patch: Record<string, unknown>) => {
    setSaveError(null)
    try {
      setTest(await bettingApi.updateTest(testId, patch))
    } catch (err) {
      setSaveError(errorMessage(err))
    }
  }

  // Le ticket se télécharge par l'API avec le jeton : il porte des captures et ne
  // peut pas être servi par simple lien.
  const [ticketBusy, setTicketBusy] = useState(false)
  const downloadTicket = async () => {
    if (!test) return
    setSaveError(null)
    setTicketBusy(true)
    try {
      await bettingApi.downloadTicket(testId, test.testNumber)
    } catch (err) {
      setSaveError(errorMessage(err))
    } finally {
      setTicketBusy(false)
    }
  }

  const order = useMemo(readOrder, [])
  const position = order.indexOf(testId)
  const prevId = position > 0 ? order[position - 1] : null
  const nextId = position >= 0 && position < order.length - 1 ? order[position + 1] : null

  const pager = (
    <div className="page-actions">
      <button type="button" className="btn btn-secondary" onClick={() => navigate('betting/tests')}>
        {t('bet.allTests')}
      </button>
      {position >= 0 && (
        <>
          <button type="button" className="btn btn-secondary" disabled={!prevId} onClick={() => prevId && navigate(`betting/tests/${prevId}`)}>
            {t('bet.prevTest')}
          </button>
          <button type="button" className="btn btn-secondary" disabled={!nextId} onClick={() => nextId && navigate(`betting/tests/${nextId}`)}>
            {t('bet.nextTest')}
          </button>
        </>
      )}
      <button type="button" className="btn btn-secondary" onClick={() => window.print()}>{t('bet.print')}</button>
      <button type="button" className="btn btn-secondary" disabled={!test || ticketBusy} onClick={downloadTicket}>
        {ticketBusy ? t('bet.ticketBusy') : t('bet.ticket')}
      </button>
    </div>
  )

  if (error) return <>{pager}<ErrorState message={error} onRetry={load} /></>
  if (!test) return <div className="stat-skeleton" style={{ minHeight: 420 }} />

  const running = test.runStatus === 'PENDING' || test.runStatus === 'RUNNING'
  const service = test.service
  const subtitle = [service?.operatorCompany, countryLabel(vocab, test.country, t), test.carrierName].filter(Boolean).join(' · ')

  return (
    <article className="bet-detail-page" aria-labelledby="bet-detail-title">
      <div className="bet-detail-toolbar">{pager}</div>

      <section className={`block bet-summary bet-summary-${(test.severity ?? 'unqualified').toLowerCase()}`}>
        <span className="bet-summary-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            {test.severity === 'COMPLIANT' ? (
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            ) : (
              <>
                <path d="M12 3l9.5 17h-19z" />
                <path d="M12 10v4M12 17h.01" />
              </>
            )}
          </svg>
        </span>
        <div className="bet-summary-text">
          <div className="bet-summary-title">
            <h1 id="bet-detail-title">{service?.name ?? t('bet.serviceFallback')}<FixtureBadge when={test.isFixture} /></h1>
            {subtitle && <span>{subtitle}</span>}
          </div>
          <p>{summarySentence(test)}</p>
        </div>
        <dl className="bet-summary-fields">
          <div className="field"><dt>{t('bet.colSeverity')}</dt><dd><SeverityBadge severity={test.severity} /></dd></div>
          <div className="field"><dt>{t('bet.colTriage')}</dt><dd><TriageBadge status={test.triageStatus} /></dd></div>
          <div className="field"><dt>{t('bet.testedOn')}</dt><dd className="num">{fmt.dateTime(test.testedAt ?? test.createdAt)}</dd></div>
        </dl>
      </section>

      {(running ||
        test.runStatus === 'BLOCKED' ||
        test.runStatus === 'GEO_BLOCKED' ||
        test.runStatus === 'FAILED') && (
        <div className={`bet-run-banner bet-run-banner-${test.runStatus.toLowerCase()}`} role="status">
          <RunStatusBadge status={test.runStatus} />
          <span>
            {running ? t('bet.runningNote') : test.runMessage ?? t('bet.runUnfinished')}
          </span>
        </div>
      )}
      {saveError && <ErrorState message={saveError} compact />}

      <div className="bet-detail-grid">
        <div className="bet-detail-main">
          <JourneySection test={test} onChanged={setTest} onZoom={setZoomIndex} />
          <PaymentBlock test={test} onSave={save} />

          <Block
            title={t('bet.serviceBlock')}
            action={service ? <button type="button" className="block-link" onClick={() => navigate(withQuery('betting/services', { service: service.id }))}>{t('bet.serviceRecord')}</button> : undefined}
          >
            <dl className="field-grid field-grid-3">
              <div className="field">
                <dt>{t('bet.colRunBy')}</dt>
                <dd>
                  {test.source === 'AUTO'
                    ? test.loggedIn
                      ? t('bet.runByRobotLogged')
                      : t('bet.runByRobotAnon')
                    : t('bet.runByAgent', { who: test.testedBy ?? '—' })}
                </dd>
              </div>
              <div className="field">
                <dt>{t('bet.carrier')}</dt>
                <dd>
                  <select className="bet-inline-select" aria-label={t('bet.carrier')} value={test.carrierId ?? ''} onChange={(e) => save({ carrierId: e.target.value || null })}>
                    <option value="">{t('bet.notSpecified')}</option>
                    {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </dd>
              </div>
              <div className="field"><dt>{t('bet.channel')}</dt><dd>{test.adChannel ?? '—'}</dd></div>
              <div className="field"><dt>{t('bet.connection')}</dt><dd>{test.connection ?? '—'}</dd></div>
              {test.depth && <div className="field"><dt>{t('bet.journeyDepth')}</dt><dd>{depthLabel(t, test.depth)}</dd></div>}
              {service?.mainUrl && <div className="field"><dt>{t('ads.address')}</dt><dd className="bet-url">{service.mainUrl}</dd></div>}
            </dl>
          </Block>

          <CollapsibleBlock
            title={t('bet.tracesBlock')}
            subtitle={t('bet.tracesSub', { urls: new Set(test.visitedUrls).size, domains: test.thirdPartyDomains.length })}
          >
            <div className="bet-traces">
              <div>
                <span className="panel-section-title">{t('bet.pagesVisited')}</span>
                {test.visitedUrls.length === 0 ? (
                  <p className="empty-note">{t('bet.noPageRecorded')}</p>
                ) : (
                  <div>{[...new Set(test.visitedUrls)].map((u) => <UrlRow key={u} url={u} />)}</div>
                )}
              </div>
              <div>
                <span className="panel-section-title">{t('bet.thirdPartyDomains')}</span>
                {test.thirdPartyDomains.length === 0 ? (
                  <p className="empty-note">{t('bet.noThirdParty')}</p>
                ) : (
                  <ul className="bet-domain-list">{test.thirdPartyDomains.map((d) => <li key={d}>{d}</li>)}</ul>
                )}
              </div>
              {test.runMessage && test.runStatus === 'COMPLETED' && (
                <div>
                  <span className="panel-section-title">{t('bet.robotReport')}</span>
                  <p className="field-note">{test.runMessage}</p>
                </div>
              )}
            </div>
          </CollapsibleBlock>
        </div>

        <aside className="bet-detail-side" aria-label={t('bet.sideAria')}>
          <DecisionBlock test={test} onSave={save} />
          <TreatmentBlock test={test} onChanged={load} />
        </aside>
      </div>

      {zoomIndex !== null && test.steps[zoomIndex] && (
        <EvidenceLightbox test={test} index={zoomIndex} onNavigate={setZoomIndex} onClose={() => setZoomIndex(null)} />
      )}
    </article>
  )
}

/**
 * Ce qu'il faut savoir en une phrase, tiré des données du test et jamais
 * inventé. `translate` : la fonction est appelée hors rendu.
 */
function summarySentence(test: PaymentTestDetail): string {
  const t: TFunction = translate
  const unauthorized = test.aggregators.find((a) => a.status === 'UNAUTHORIZED')
  const method = test.paymentMethods[0]?.name
  if (test.beneficiaryName) {
    const agg = test.aggregators.find((a) => a.evidence?.toLowerCase().includes(test.beneficiaryName!.toLowerCase())) ?? unauthorized
    const status =
      agg?.status === 'UNAUTHORIZED'
        ? t('bet.summaryAggUnauthorized')
        : agg?.status === 'AUTHORIZED'
          ? t('bet.summaryAggAuthorized')
          : ''
    return t('bet.summaryPayee', { method: method ? ` ${method}` : '', payee: test.beneficiaryName, status })
  }
  if (unauthorized) return t('bet.summaryRouted', { name: unauthorized.name })
  if (test.description) return test.description
  if (test.breachType) return breachLabel(t, test.breachType)
  if (test.runStatus === 'PENDING' || test.runStatus === 'RUNNING') return t('bet.summaryDetecting')
  if (test.paymentMethods.length > 0) {
    return t('bet.summaryMethods', { methods: test.paymentMethods.map((m) => m.name).join(', ') })
  }
  return t('bet.summaryEmpty')
}

function DecisionBlock({ test, onSave }: { test: PaymentTestDetail; onSave: (patch: Record<string, unknown>) => Promise<void> }) {
  const t = useT()
  const [text, setText] = useState(test.description ?? '')
  useEffect(() => setText(test.description ?? ''), [test.description])

  return (
    <Block title={t('bet.decisionBlock')}>
      <div className="form-stack">
        <label>
          {t('bet.colSeverity')}
          <select value={test.severity ?? ''} onChange={(e) => onSave({ severity: (e.target.value || null) as ComplianceSeverity | null })}>
            <option value="">{t('bet.sev.UNQUALIFIED')}</option>
            {SEVERITY_ORDER.map((s) => <option key={s} value={s}>{severityLabel(t, s)}</option>)}
          </select>
        </label>
        <label>
          {t('bet.colBreach')}
          <select value={test.breachType ?? ''} onChange={(e) => onSave({ breachType: (e.target.value || null) as BreachType | null })}>
            <option value="">{t('bet.noBreachOption')}</option>
            {BREACH_TYPES.map((b) => <option key={b} value={b}>{breachLabel(t, b)}</option>)}
          </select>
        </label>
        <label>
          {t('bet.finding')}
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('bet.findingPlaceholder')} />
        </label>
        {text !== (test.description ?? '') && (
          <div className="bet-form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setText(test.description ?? '')}>{t('common.cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={() => onSave({ description: text.trim() || null })}>{t('bet.saveFinding')}</button>
          </div>
        )}
      </div>
      <p className="bet-assessed">
        {test.assessedBy === 'robot'
          ? t('bet.assessedByRobot')
          : test.assessedBy
            ? t('bet.assessedBy', { who: test.assessedBy })
            : t('bet.notAssessed')}
      </p>
    </Block>
  )
}

/** Traitement dans la page : statut, agent en charge, motif et historique, sans fenêtre modale. */
function TreatmentBlock({ test, onChanged }: { test: PaymentTestDetail; onChanged: () => void }) {
  const { t, fmt } = useI18n()
  const relativeDays = useRelativeDays()
  const [vocab, setVocab] = useState<TriageVocabulary | null>(null)
  const [agents, setAgents] = useState<Array<{ id: string; fullName: string; email: string }>>([])
  const [target, setTarget] = useState<TriageStatus | null>(null)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [log, setLog] = useState<TriageLogEntry[] | null>(test.triageLog ?? null)

  useEffect(() => {
    triageApi.getVocabulary().then(setVocab).catch(() => setVocab(null))
    authApi.getAgents().then(setAgents).catch(() => setAgents([]))
  }, [])

  useEffect(() => {
    if (test.triageLog) setLog(test.triageLog)
    else triageApi.getLog('betting', test.id).then(setLog).catch(() => setLog([]))
  }, [test.id, test.triageLog, test.triageStatus, test.assignedTo])

  const spec = vocab?.statuses.find((s) => s.value === test.triageStatus)
  const nextOptions = spec?.allowedNext ?? []
  const commentRequired = target ? (vocab?.statuses.find((s) => s.value === target)?.commentRequired ?? false) : false
  const agentName = (value: string | null) => agents.find((a) => a.email === value)?.fullName ?? value

  const submit = async () => {
    if (!target) return
    setBusy(true)
    setError(null)
    try {
      await triageApi.changeStatus('betting', test.id, target, comment.trim() || undefined)
      setTarget(null)
      setComment('')
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const assign = async (assignee: string | null) => {
    setBusy(true)
    setError(null)
    try {
      await triageApi.assign('betting', test.id, assignee)
      onChanged()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const since = test.statusChangedAt ?? test.createdAt
  const primary = nextOptions.find((s) => s === 'CONFIRMED') ?? nextOptions.find((s) => s === 'IN_REVIEW')
  const assignedKnown = !test.assignedTo || agents.some((a) => a.email === test.assignedTo)

  return (
    <>
      <Block title={t('bet.treatmentBlock')}>
        <div className="bet-treatment-status">
          <TriageBadge status={test.triageStatus} />
          <span className="block-sub">{relativeDays(since)}</span>
        </div>
        <div className="form-stack">
          <label>
            {t('ads.agentInCharge')}
            <select value={test.assignedTo ?? ''} disabled={busy} onChange={(e) => assign(e.target.value || null)}>
              <option value="">{t('bet.notAssigned')}</option>
              {!assignedKnown && <option value={test.assignedTo!}>{test.assignedTo}</option>}
              {agents.map((a) => <option key={a.id} value={a.email}>{a.fullName}</option>)}
            </select>
          </label>
        </div>

        {nextOptions.length > 0 && !target && (
          <div className="bet-treatment-actions">
            {primary && (
              <button type="button" className="btn btn-primary" onClick={() => setTarget(primary)}>{enumLabel(t, 'triage', primary, primary)}</button>
            )}
            {nextOptions.filter((s) => s !== primary).map((s) => (
              <button key={s} type="button" className="btn btn-secondary" onClick={() => setTarget(s)}>{enumLabel(t, 'triage', s, s)}</button>
            ))}
          </div>
        )}

        {target && (
          <div className="bet-treatment-confirm form-stack">
            <label>
              {t('bet.transitionLabel', {
                from: enumLabel(t, 'triage', test.triageStatus, test.triageStatus),
                to: enumLabel(t, 'triage', target, target),
                requirement: commentRequired ? t('bet.required') : t('bet.optional'),
              })}
              <textarea rows={3} value={comment} autoFocus onChange={(e) => setComment(e.target.value)} placeholder={t('bet.reasonKeptHistory')} />
            </label>
            <div className="bet-form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setTarget(null); setComment('') }}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-primary" disabled={busy || (commentRequired && !comment.trim())} onClick={submit}>
                {busy ? t('common.saving') : t('bet.validate')}
              </button>
            </div>
          </div>
        )}
        {error && <ErrorState message={error} compact />}
      </Block>

      <Block title={t('bet.historyBlock')}>
        <ol className="bet-history">
          {[...(log ?? [])].reverse().map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <span className="bet-history-dot" aria-hidden="true" />
              <div>
                <span className="bet-history-what">
                  {e.from === e.to
                    ? e.comment
                    : `${enumLabel(t, 'triage', e.from, e.from)} → ${enumLabel(t, 'triage', e.to, e.to)}`}
                </span>
                {e.from !== e.to && e.comment && <span className="bet-history-comment">{e.comment}</span>}
                <span className="bet-history-meta">{agentName(e.agent)} · {fmt.dateTime(e.at)}</span>
              </div>
            </li>
          ))}
          <li>
            <span className="bet-history-dot" aria-hidden="true" />
            <div>
              <span className="bet-history-what">{t('bet.testCreated')}</span>
              <span className="bet-history-meta">
                {test.source === 'AUTO' ? t('bet.source.AUTO') : agentName(test.testedBy) ?? t('bet.source.MANUAL')} · {fmt.dateTime(test.createdAt)}
              </span>
            </div>
          </li>
        </ol>
      </Block>
    </>
  )
}

function UrlRow({ url }: { url: string }) {
  const t = useT()
  const [copied, setCopied] = useState(false)
  return (
    <div className="bet-url-row">
      <span className="bet-url">{url}</span>
      <button
        type="button"
        className="btn btn-secondary bet-copy"
        onClick={() => {
          navigator.clipboard?.writeText(url).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          })
        }}
      >
        {copied ? t('bet.copied') : t('common.copy')}
      </button>
    </div>
  )
}

// ── Parcours et preuves ──────────────────────────────────────────────────────

function JourneySection({
  test,
  onChanged,
  onZoom,
}: {
  test: PaymentTestDetail
  onChanged: (t: PaymentTestDetail) => void
  onZoom: (index: number) => void
}) {
  const t = useT()
  const fileRef = useRef<HTMLInputElement>(null)
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setBusy(true)
    setError(null)
    try {
      let latest: PaymentTestDetail | null = null
      for (const file of Array.from(files)) {
        latest = await bettingApi.uploadEvidence(test.id, file, label.trim() || file.name.replace(/\.[^.]+$/, ''))
      }
      if (latest) onChanged(latest)
      setLabel('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const move = async (index: number, delta: number) => {
    const steps = [...test.steps]
    const target = index + delta
    if (target < 0 || target >= steps.length) return
    ;[steps[index], steps[target]] = [steps[target], steps[index]]
    try {
      onChanged(await bettingApi.updateSteps(test.id, steps.map((s) => ({ evidenceKey: s.evidenceKey, label: s.label }))))
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const who = test.source === 'AUTO' ? t('bet.journeyByRobot') : t('bet.journeyByAgent')

  return (
    <Block
      title={t('bet.journeyBlock')}
      subtitle={t('bet.journeySub', { n: test.steps.length, who })}
      action={
        <div className="bet-upload">
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('bet.captionPlaceholder')} aria-label={t('bet.captionAria')} />
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} />
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? t('bet.uploading') : t('bet.addCapture')}
          </button>
        </div>
      }
    >
      {error && <ErrorState message={error} compact />}
      {test.steps.length === 0 ? (
        <p className="empty-note">
          {test.source === 'AUTO' ? t('bet.noStepRobot') : t('bet.noStepAgent')}
        </p>
      ) : (
        <ol className="bet-steps">
          {test.steps.map((step, i) => (
            <li key={`${step.evidenceKey}-${i}`} className="bet-step">
              <EvidenceImage
                testId={test.id}
                evidenceKey={step.evidenceKey}
                alt={t('bet.stepAlt', { n: step.order, label: step.label })}
                className="bet-step-img"
                onClick={step.evidenceKey ? () => onZoom(i) : undefined}
              />
              <div className="bet-step-caption">
                <span className="bet-step-num num">{step.order}</span>
                <span className="bet-step-label">{step.label}</span>
              </div>
              <div className="bet-step-meta">
                <span>{step.auto ? t('bet.source.AUTO') : t('bet.source.MANUAL')}</span>
                <span className="bet-step-order">
                  <button type="button" aria-label={t('bet.stepMoveUp', { n: step.order })} disabled={i === 0} onClick={() => move(i, -1)}>‹</button>
                  <button type="button" aria-label={t('bet.stepMoveDown', { n: step.order })} disabled={i === test.steps.length - 1} onClick={() => move(i, 1)}>›</button>
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Block>
  )
}

function EvidenceLightbox({
  test,
  index,
  onNavigate,
  onClose,
}: {
  test: PaymentTestDetail
  index: number
  onNavigate: (i: number) => void
  onClose: () => void
}) {
  const t = useT()
  const step = test.steps[index]
  useEscapeKey(true, onClose)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && index < test.steps.length - 1) onNavigate(index + 1)
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, test.steps.length, onNavigate])

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="bet-lightbox" role="dialog" aria-modal="true" aria-label={t('bet.stepLabel', { n: step.order })} onClick={(e) => e.stopPropagation()}>
        <div className="bet-lightbox-bar">
          <span><strong className="num">{step.order}/{test.steps.length}</strong> {step.label}</span>
          <button type="button" className="shell-icon-btn" aria-label={t('common.close')} onClick={onClose}><Icon name="close" /></button>
        </div>
        <EvidenceImage testId={test.id} evidenceKey={step.evidenceKey} alt={step.label} className="bet-lightbox-img" />
        <div className="bet-lightbox-bar">
          <button type="button" className="btn btn-secondary" disabled={index === 0} onClick={() => onNavigate(index - 1)}>{t('bet.prevCapture')}</button>
          <span className="bet-url">{step.url ?? t('bet.agentCapture')}</span>
          <button type="button" className="btn btn-secondary" disabled={index === test.steps.length - 1} onClick={() => onNavigate(index + 1)}>{t('bet.nextCapture')}</button>
        </div>
      </div>
    </div>
  )
}

// ── Paiement : ce qui a été payé, à qui ──────────────────────────────────────

function PaymentBlock({ test, onSave }: { test: PaymentTestDetail; onSave: (patch: Record<string, unknown>) => Promise<void> }) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [sms, setSms] = useState(test.smsText ?? '')
  const [beneficiary, setBeneficiary] = useState(test.beneficiaryName ?? '')
  const [amount, setAmount] = useState(test.amount ?? '')

  useEffect(() => {
    setSms(test.smsText ?? '')
    setBeneficiary(test.beneficiaryName ?? '')
    setAmount(test.amount ?? '')
  }, [test.smsText, test.beneficiaryName, test.amount])

  const dirty = sms !== (test.smsText ?? '') || beneficiary !== (test.beneficiaryName ?? '') || amount !== (test.amount ?? '')
  const [firstMethod, ...otherMethods] = test.paymentMethods
  const beneficiaryAgg = test.beneficiaryName
    ? test.aggregators.find((a) => a.evidence?.toLowerCase().includes(test.beneficiaryName!.toLowerCase()))
    : undefined

  return (
    <Block
      title={t('bet.paymentBlock')}
      subtitle={test.smsText ? t('bet.smsEvidence') : t('bet.noSms')}
      action={
        <button type="button" className="block-link" onClick={() => setEditing((v) => !v)}>
          {editing ? t('bet.closeEntry') : test.smsText ? t('bet.editSms') : t('bet.pasteSms')}
        </button>
      }
    >
      <dl className="field-grid field-grid-3">
        <div className="field">
          <dt>{t('bet.paymentMethod')}</dt>
          <dd className="field-value-strong">{firstMethod?.name ?? '—'}</dd>
          {otherMethods.length > 0 && (
            <span className="field-note">{t('bet.alsoOffered', { methods: otherMethods.map((m) => m.name).join(', ') })}</span>
          )}
        </div>
        <div className="field">
          <dt>{t('bet.actualPayee')}</dt>
          <dd className="field-value-strong">{test.beneficiaryName ?? '—'}</dd>
          {beneficiaryAgg && <span className="field-note"><AggregatorStatusPill status={beneficiaryAgg.status} /></span>}
        </div>
        <div className="field">
          <dt>{t('bet.amount')}</dt>
          <dd className="field-value-strong num">{test.amount ?? '—'}</dd>
        </div>
      </dl>

      {test.smsText && !editing && <div className="bet-sms-quote">{test.smsText}</div>}

      {editing && (
        <div className="form-stack bet-sms-edit">
          <label>
            {t('bet.smsLabel')}
            <textarea rows={3} value={sms} onChange={(e) => setSms(e.target.value)} placeholder={t('bet.smsPlaceholder')} />
          </label>
          <div className="bet-form-row">
            <label>
              {t('bet.payee')}
              <input value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} placeholder={t('bet.payeeFromSms')} />
            </label>
            <label>
              {t('bet.amount')}
              <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('bet.amountPlaceholder')} />
            </label>
          </div>
          <div className="bet-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dirty}
              onClick={async () => {
                await onSave({
                  smsText: sms || null,
                  // Le bénéficiaire n'est envoyé que s'il a été modifié : sinon le serveur le relit dans le SMS.
                  ...(beneficiary !== (test.beneficiaryName ?? '') ? { beneficiaryName: beneficiary || null } : {}),
                  ...(amount !== (test.amount ?? '') ? { amount: amount || null } : {}),
                })
                setEditing(false)
              }}
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      )}

      <div className="bet-agg-block">
        <span className="panel-section-title">{t('bet.aggIdentified')}</span>
        {test.aggregators.length === 0 ? (
          <p className="empty-note">{t('bet.noAggIdentified')}</p>
        ) : (
          <ul className="bet-agg-list">
            {test.aggregators.map((a) => (
              <li key={`${a.name}-${a.evidence}`}>
                <div>
                  <strong>{a.name}</strong>
                  <span>{a.evidence}</span>
                </div>
                <AggregatorStatusPill status={a.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Block>
  )
}

